import { Spinner } from '@blueprintjs/core';
import { DEMO_CONNECTION_INDEX } from 'portal-sdk';
import * as React from 'react';
import { usePortalConnections, usePortalUser } from 'vet-bones/utils/portal';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { ROUTE } from 'src/constants/routes';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectEndpointDialog } from 'src/containers/ProjectEndpointDialog/ProjectEndpointDialog';
import { pendingFileUploadsSelector } from 'src/selectors/pendingFileUploads';
import { pendingVirtualGraphsSelector } from 'src/selectors/pendingVirtualGraphs';
import { projectSelector } from 'src/selectors/project';
import * as collaborationProvider from 'src/utils/collaboration/collaborationProvider';
import { useCloudStorageBackupProjects } from 'src/utils/hooks/useCloudStorageBackupProjects';
import { useCloudStorageProject } from 'src/utils/hooks/useCloudStorageProject';
import { useCollaborationConnection } from 'src/utils/hooks/useCollaborationConnection';
import { useDefaultPortalConnection } from 'src/utils/hooks/useDefaultPortalConnection';
import { useEnsureConnection } from 'src/utils/hooks/useEnsureConnection';
import { useNavigate } from 'src/utils/hooks/useNavigate';
import { usePortalConnectionsWithDemo } from 'src/utils/hooks/usePortalConnectionsWithDemo';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { redirectToPortalDashboard } from 'src/utils/redirectToPortalDashboard';

type ProjectRedirectWrapperProps = {
  children?: React.ReactNode;
};

export const ProjectRedirectWrapper: React.VFC<ProjectRedirectWrapperProps> = ({
  children,
}) => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  const portalUser = usePortalUser();
  const connections = usePortalConnections();
  const portalConnections = usePortalConnectionsWithDemo();
  const defaultConnection = useDefaultPortalConnection();

  const { isConnected, isConnectionPending, isDisconnected, ensureConnection } =
    useEnsureConnection();

  const { data: cloudProject, isLoading } = useCloudStorageProject(projectId);
  useCloudStorageBackupProjects();

  // Open/close the collaboration WebSocket with the project route: connect when
  // this project is opened, disconnect when leaving the project path or
  // switching projects.
  useCollaborationConnection(projectId);

  const project = useReduxSelector(projectSelector(projectId));
  const pendingFileUploads = useReduxSelector(
    pendingFileUploadsSelector(projectId)
  );
  const pendingVirtualGraphs = useReduxSelector(
    pendingVirtualGraphsSelector(projectId)
  );
  const hasProject = project && !project.isRemoved;
  const projectConnectionId = project?.connectionId;
  const projectConnection = portalConnections?.find(
    (c) => c.id === projectConnectionId
  );

  const missingProjectDetails = React.useMemo(
    () => ({ id: projectId, name: project?.name }),
    [project?.name, projectId]
  );

  const hasAdditionalConnections = Boolean(
    portalConnections?.find(
      (c) => c.index !== DEMO_CONNECTION_INDEX && c.id !== defaultConnection?.id
    )
  );

  React.useEffect(() => {
    if (!isDisconnected) {
      return;
    }
    ensureConnection();
    navigate(ROUTE.HOME);
  }, [isDisconnected, ensureConnection, navigate]);

  React.useEffect(() => {
    if (!cloudProject?.project) {
      return;
    }
    // Skip re-dispatch while collaboration is active — the Y.Doc is the
    // source of truth and re-dispatching would replace the project in Redux,
    // generate a new diagram.id, and cause GoJS to rebuild (visible flash).
    // When collaboration is disconnected (timeout, network drop), allow the
    // refetch to update state so the user gets the latest cloud data.
    if (hasProject && collaborationProvider.isConnected(projectId)) {
      return;
    }
    dispatch(
      projectsActionCreators.loadedProjectFromCloud(cloudProject.project)
    );
  }, [cloudProject, dispatch, hasProject, projectId]);

  // Handle pending file uploads from project creation flow
  React.useEffect(() => {
    if (!hasProject || !projectId || !pendingFileUploads?.length) {
      return;
    }

    dispatch(
      projectsActionCreators.addedFileUploadsToProject(
        projectId,
        pendingFileUploads
      )
    );
    dispatch(projectsActionCreators.clearedPendingFileUploads(projectId));
  }, [dispatch, hasProject, pendingFileUploads, projectId]);

  // Handle pending virtual graphs from project creation flow
  React.useEffect(() => {
    if (!hasProject || !projectId || !pendingVirtualGraphs?.length) {
      return;
    }

    dispatch(
      projectsActionCreators.addedVirtualGraphsToProject(
        projectId,
        ...pendingVirtualGraphs
      )
    );
    dispatch(projectsActionCreators.clearedPendingVirtualGraphs(projectId));
  }, [dispatch, hasProject, pendingVirtualGraphs, projectId]);

  React.useEffect(() => {
    if (isLoading || !cloudProject || !portalUser.data || !connections.data) {
      // ignore if we're loading cloud project, user feature flags, or connections
      return;
    }

    if (!hasProject) {
      if (!cloudProject.project) {
        // project no longer exists both locally and in cloud
        dispatch(
          projectsActionCreators.openedMissingProject(missingProjectDetails)
        );
        navigate(ROUTE.HOME);
      }

      // there's a mismatch between whether the project was removed between
      // local version and cloud version, do not redirect
      return;
    }

    if (projectConnection && defaultConnection) {
      redirectToPortalDashboard(defaultConnection, projectConnection);
    }

    if (!projectConnection && !hasAdditionalConnections && defaultConnection) {
      dispatch(
        projectsActionCreators.updatedProjectConnectionId(
          projectId,
          defaultConnection.id
        )
      );
    }
  }, [
    cloudProject,
    connections,
    defaultConnection,
    dispatch,
    hasAdditionalConnections,
    hasProject,
    isLoading,
    missingProjectDetails,
    navigate,
    projectConnection,
    projectConnectionId,
    projectId,
    portalUser,
  ]);

  if (isDisconnected) {
    return (
      <Spinner
        data-testid={TEST_IDS.projectRedirectWrapper.disconnectedSpinner}
      />
    );
  }

  if (portalUser.data && connections.data && hasProject) {
    if (!projectConnectionId || !projectConnection) {
      // Wait for the cloud fetch to settle before deciding the connection is
      // missing — a still-loading or stale connection id would otherwise flash
      // the endpoint dialog for a frame before the authoritative connection
      // resolves.
      if (isLoading) {
        return (
          <Spinner
            data-testid={TEST_IDS.projectRedirectWrapper.loadingSpinner}
          />
        );
      }
      // project does not have a valid connection, open the endpoint dialog
      return <ProjectEndpointDialog />;
    }

    if (projectConnectionId === defaultConnection?.id) {
      // project connection is valid and matches the curernt connection
      // show the page contents
      return <>{children}</>;
    }
  }

  // othrwise, show the loading indicator
  // this will occur when the project data, user features, connections, are loading
  // or after a project endpoint has been confirmed and waiting for a redirect
  return <Spinner />;
};
